// WWI munitions factory: a brick industrial hall under a dark gable roof with
// bold hazard-yellow accents (ridge, roof stripes, stack band — the logistics/
// munitions code), a tall smokestack as the icon, shell stacks and an outdoor
// assembly bench in the open work yard. 3x3 tiles.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, gable, slab, poles } = styleV3;

const BRICK = "#9a5f43";
const GUNMETAL = "#4a4d52";
const TIMBER = "#8b6438";
const TIMBER_DK = "#6a4c2a";
const HAZARD_Y = "#e8c832";    // hazard yellow — the munitions accent
const HAZARD_DK = "#2e2e30";
const SHADOW = "#332e26";

const BASE = 0.12;                       // foundation slab top
// The hall fills the west side of the 3x3 plot; the (+x) work yard is open.
const HX = -0.6, HZ = -0.2, HW = 1.5, HD = 1.95;
const WALL_H = 0.75;
const EAST = HX + HW / 2;                // the hall's yard-facing +x face
const STACK_X = -1.05, STACK_Z = -0.85;

function hall(h: number): Part {
  return box(HW, h, HD, BRICK, [HX, BASE + h / 2, HZ]);
}

// The dark gable roof (ridge along z) with hazard-yellow ridge cap and two
// yellow stripes laid down the east slope.
function roof(): Part[] {
  const slope = Math.atan2(0.48, 0.85);
  const stripe = (z: number): Part =>
    box(0.7, 0.03, 0.3, HAZARD_Y, [HX + 0.47, 1.09, z], [0, 0, -slope]);
  return [
    gable(1.7, HD + 0.2, 0.48, GUNMETAL, HX, BASE + WALL_H, HZ, "z"),
    box(0.14, 0.06, HD + 0.25, HAZARD_Y, [HX, BASE + WALL_H + 0.48, HZ]),
    stripe(0.35), stripe(-0.75),
  ];
}

// The tall brick smokestack — the signature vertical — with a yellow band.
function smokestack(h: number): Part[] {
  const parts: Part[] = [cyl(0.13, 0.17, h, 8, BRICK, [STACK_X, BASE + h / 2, STACK_Z])];
  if (h > 1.4) {
    parts.push(cyl(0.145, 0.145, 0.14, 8, HAZARD_Y, [STACK_X, BASE + h - 0.18, STACK_Z]));
    parts.push(cyl(0.15, 0.15, 0.08, 8, HAZARD_DK, [STACK_X, BASE + h - 0.04, STACK_Z]));
  }
  return parts;
}

// The big loading doorway opening onto the yard, with a yellow lintel.
function doorway(): Part[] {
  return [
    box(0.06, 0.5, 0.5, SHADOW, [EAST + 0.02, BASE + 0.25, HZ]),
    box(0.08, 0.08, 0.6, HAZARD_Y, [EAST + 0.03, BASE + 0.54, HZ]),
  ];
}

// The outdoor assembly bench where shells are fitted and fuzed.
function bench(): Part[] {
  return [
    box(0.75, 0.07, 0.32, TIMBER, [0.8, BASE + 0.36, -0.55]),
    box(0.08, 0.32, 0.26, TIMBER_DK, [0.52, BASE + 0.16, -0.55]),
    box(0.08, 0.32, 0.26, TIMBER_DK, [1.08, BASE + 0.16, -0.55]),
    cyl(0.045, 0.045, 0.16, 6, GUNMETAL, [0.68, BASE + 0.475, -0.55]),
    cyl(0.045, 0.045, 0.16, 6, GUNMETAL, [0.92, BASE + 0.475, -0.55]),
  ];
}

// Finished shells racked in the yard: two sleepers, a 3+2 pyramid of shells
// lying north-south, each with a hazard-yellow nose band.
function shellStack(): Part[] {
  const parts: Part[] = [
    box(0.55, 0.06, 0.08, TIMBER_DK, [0.55, BASE + 0.03, 0.39]),
    box(0.55, 0.06, 0.08, TIMBER_DK, [0.55, BASE + 0.03, 0.71]),
  ];
  const shell = (x: number, y: number): Part[] => [
    cyl(0.055, 0.055, 0.5, 7, GUNMETAL, [x, BASE + y, 0.55], [Math.PI / 2, 0, 0]),
    cyl(0.057, 0.057, 0.06, 7, HAZARD_Y, [x, BASE + y, 0.76], [Math.PI / 2, 0, 0]),
  ];
  for (const x of [0.4, 0.55, 0.7]) parts.push(...shell(x, 0.115));
  for (const x of [0.475, 0.625]) parts.push(...shell(x, 0.215));
  return parts;
}

// Crates by the door and hazard bollards marking the yard corner.
function yardDressing(): Part[] {
  const bollard = (x: number, z: number): Part[] => [
    cyl(0.045, 0.045, 0.28, 6, HAZARD_Y, [x, BASE + 0.14, z]),
    cyl(0.05, 0.05, 0.06, 6, HAZARD_DK, [x, BASE + 0.2, z]),
  ];
  return [
    box(0.24, 0.24, 0.24, TIMBER_DK, [0.35, BASE + 0.12, 0.32]),
    box(0.18, 0.18, 0.18, TIMBER_DK, [0.35, BASE + 0.33, 0.32]),
    ...bollard(1.2, 1.0), ...bollard(1.2, -1.0),
  ];
}

export const munitionsFactory = defineModel({
  id: "munitions-factory",
  tiles: { w: 3, h: 3 },
  batchId: "wwi",
  name: "Munitions Factory",
  tags: ["building", "wwi", "military", "factory", "industry", "munitions"],
  summary: "A brick shell factory with hazard-yellow roof accents and a tall stack.",
  description:
    "Brick industrial hall turning out artillery shells: a dark gable roof " +
    "striped hazard yellow, a tall banded smokestack as the icon, and an open " +
    "work yard with an outdoor assembly bench and racked shell stacks.",
  author: "Fable 5",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [slab(2.9, 2.9)] },
    { id: "lower", label: "Lower", parts: () => [
      slab(2.9, 2.9), hall(0.32), ...smokestack(0.7), ...poles(2.7, 2.7, 1.0),
    ] },
    { id: "upper", label: "Upper", parts: () => [
      slab(2.9, 2.9), hall(WALL_H), ...smokestack(1.25), ...doorway(), ...poles(2.7, 2.7, 1.5),
    ] },
    { id: "complete", label: "Complete", parts: () => [
      slab(2.9, 2.9), hall(WALL_H), ...roof(), ...smokestack(1.75), ...doorway(),
      ...bench(), ...shellStack(), ...yardDressing(),
    ] },
  ],
  animations: [],
  // Where a worker stands to assemble shells: at the outdoor bench on open
  // ground, facing the benchtop.
  stations: [
    { id: "assemble", position: [0.8, BASE, -0.2], facing: Math.PI, description: "Where a worker assembles shells at the outdoor bench." },
  ],
  interactions: [
    { id: "assemble-shells", label: "Assemble shells", station: "assemble", variants: ["complete"], ...benchWork() },
  ],
});
